import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { haptic } from "../telegram";
import type { Gender, Location, Me } from "../types";
import { useBackNavigation } from "../use-back-navigation";

export function Onboarding({
  onComplete,
  initialProfile,
}: {
  onComplete(): Promise<void>;
  initialProfile: Me["profile"];
}) {
  const [step, setStep] = useState<"profile" | "photo">(initialProfile ? "photo" : "profile");
  const [locations, setLocations] = useState<Location[]>([]);
  const [displayName, setDisplayName] = useState(initialProfile?.displayName ?? "");
  const [birthDate, setBirthDate] = useState(initialProfile?.birthDate.slice(0, 10) ?? "");
  const [gender, setGender] = useState<Gender>(initialProfile?.gender ?? "female");
  const [interestedIn, setInterestedIn] = useState<Gender[]>(["male"]);
  const [locationId, setLocationId] = useState(initialProfile?.location.id ?? "");
  const [bio, setBio] = useState(initialProfile?.bio ?? "");
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const [cities, preferences] = await Promise.all([
        api<{ locations: Location[] }>("/v1/locations"),
        initialProfile ? api<{ interestedIn: Gender[] }>("/v1/me/preferences") : Promise.resolve(null),
      ]);
      setLocations(cities.locations);
      setLocationId((current) => current || cities.locations[0]?.id || "");
      if (preferences) setInterestedIn(preferences.interestedIn);
      setLoaded(true);
    } catch {
      setLoadError(true);
    }
  }, [initialProfile]);
  useEffect(() => {
    void load();
  }, [load]);
  useBackNavigation(() => setStep("profile"), step === "photo" && !busy);
  const preview = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const maxAdult = () => {
    const n = new Date();
    return new Date(Date.UTC(n.getUTCFullYear() - 18, n.getUTCMonth(), n.getUTCDate())).toISOString().slice(0, 10);
  };
  const toggleInterest = (value: Gender) =>
    setInterestedIn((current) => (current.includes(value) ? current.filter((x) => x !== value) : [...current, value]));

  const submitProfile = async () => {
    if (busy) return;
    setError("");
    if (
      displayName.trim().length < 2 ||
      !birthDate ||
      birthDate > maxAdult() ||
      !locationId ||
      interestedIn.length === 0
    ) {
      setError("Заполните обязательные поля.");
      return;
    }
    setBusy(true);
    try {
      await api("/v1/me/onboarding", {
        method: "PUT",
        body: JSON.stringify({ displayName, birthDate, gender, interestedIn, locationId, bio: bio || undefined }),
      });
      setStep("photo");
      haptic("success");
    } catch {
      setError("Не получилось сохранить профиль. Проверьте данные и попробуйте снова.");
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async () => {
    if (busy) return;
    if (!photo && !photoUploaded) {
      setError("Выберите основную фотографию.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (!photoUploaded && photo) {
        const form = new FormData();
        form.append("photo", photo);
        await api("/v1/me/photos", { method: "POST", body: form });
        setPhotoUploaded(true);
      }
      haptic("success");
      try {
        await onComplete();
      } catch {
        setError("Фото сохранено. Не удалось обновить профиль. Нажмите «Обновить профиль».");
      }
    } catch {
      setError("Не удалось обработать фото. Используйте JPG, PNG или WebP до 10 МБ.");
    } finally {
      setBusy(false);
    }
  };

  if (step === "photo")
    return (
      <main className="onboarding">
        <button type="button" className="section-back" disabled={busy} onClick={() => setStep("profile")}>
          ‹ Назад
        </button>
        <div className="progress">
          <span style={{ width: "100%" }} />
        </div>
        <div className="onboarding-copy">
          <span className="eyebrow">Шаг 2 из 2</span>
          <h1>Добавьте главное фото</h1>
          <p>
            Лучше всего — ясный портрет без текста и тяжёлых фильтров. Фото проходит проверку перед показом другим
            людям.
          </p>
        </div>
        <label className="photo-picker">
          {preview ? (
            <img src={preview} alt="Предпросмотр фотографии" />
          ) : (
            <div>
              <strong>Выбрать фото</strong>
              <span>JPG, PNG или WebP · до 10 МБ</span>
            </div>
          )}
          <input
            type="file"
            disabled={busy}
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => {
              setPhoto(e.target.files?.[0] ?? null);
              setPhotoUploaded(false);
            }}
          />
        </label>
        {error && <div className="inline-error">{error}</div>}
        <button type="button" className="primary-button" disabled={busy} onClick={uploadPhoto}>
          {busy ? "Обрабатываем…" : photoUploaded ? "Обновить профиль" : "Готово"}
        </button>
      </main>
    );

  return (
    <main className="onboarding">
      <div className="progress">
        <span style={{ width: "50%" }} />
      </div>
      <div className="onboarding-copy">
        <span className="eyebrow">Шаг 1 из 2</span>
        <h1>Расскажите немного о себе</h1>
        <p>Только то, что помогает начать знакомство. Остальное можно добавить позже.</p>
      </div>
      <fieldset className="form-grid onboarding-fields" disabled={busy}>
        <label>
          <span>Имя</span>
          <input
            value={displayName}
            maxLength={50}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Как вас называть?"
          />
        </label>
        <label>
          <span>Дата рождения</span>
          <input type="date" max={maxAdult()} value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
          <small>DonDate — только для пользователей 18+.</small>
        </label>
        <fieldset>
          <legend>Ваш пол</legend>
          <div className="segmented">
            {(
              [
                ["female", "Женщина"],
                ["male", "Мужчина"],
                ["other", "Другое"],
              ] as const
            ).map(([value, label]) => (
              <button
                type="button"
                className={gender === value ? "active" : ""}
                onClick={() => setGender(value)}
                key={value}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>Кого хотите видеть</legend>
          <div className="chips">
            {(
              [
                ["female", "Женщин"],
                ["male", "Мужчин"],
                ["other", "Других"],
              ] as const
            ).map(([value, label]) => (
              <button
                type="button"
                className={interestedIn.includes(value) ? "active" : ""}
                onClick={() => toggleInterest(value)}
                key={value}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>
        <label>
          <span>Город</span>
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>
            О себе <em>необязательно</em>
          </span>
          <textarea
            value={bio}
            maxLength={500}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Пара фраз для первого сообщения"
          />
        </label>
      </fieldset>
      {loadError && (
        <div className="inline-error" role="alert">
          Не удалось загрузить города и настройки.
          <button type="button" className="secondary-button" onClick={() => void load()}>
            Повторить загрузку
          </button>
        </div>
      )}
      {error && (
        <div className="inline-error" role="alert">
          {error}
        </div>
      )}
      <button type="button" className="primary-button" disabled={busy || !loaded} onClick={submitProfile}>
        {busy ? "Сохраняем…" : "Продолжить"}
      </button>
    </main>
  );
}
